// frontend/app/utils/davinciCertHtml.js
//
// Shared Da Vinci 3-Day Challenge certificate template ("Celestial Honors").
//
// The certificate HTML, layout constants, and asset/model maps were extracted
// verbatim from DavinciCertificate.js so the Certificate Wall can render the
// EXACT same template the scanner prints (true WYSIWYG). The 3D-model snapshot
// logic lives in davinciModels.js — both this template and the wall feed it
// their subjects and drop the resulting PNGs into buildCertHtml.
//
// Web/kiosk only (the rendered HTML needs WebGL + the model-viewer script +
// Google Fonts), but this module itself is pure string/asset work and safe to
// import anywhere.

import { Image } from "react-native";

export const DAVINCI_LOGO = require("../../assets/images/davinci.png");
export const SARAS_LOGO = require("../../assets/Saras-3D_Logo.png");

export const MODELS = {
  Heart: require("../../assets/models/Heart.glb"),
  Diamond: require("../../assets/models/Diamond.glb"),
  Pendulum_waves: require("../../assets/models/Pendulum_waves.glb"),
  Brain: require("../../assets/models/Brain.glb"),
  Graphite: require("../../assets/models/Graphite.glb"),
};

export const MODEL_VIEWER_SRC = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
export const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500&family=Jost:wght@400;500;600&family=Pinyon+Script&display=swap";

export const resolveUri = (mod) => {
  if (!mod) return "";
  if (typeof mod === "string") return mod;
  if (mod.uri) return mod.uri;
  try {
    return Image.resolveAssetSource(mod)?.uri || "";
  } catch {
    return "";
  }
};

export const subjectToModel = (subject) => {
  const s = (subject || "").toLowerCase();
  if (s.includes("bio")) return { key: "Heart", label: subject };
  if (s.includes("chem")) return { key: "Diamond", label: subject };
  if (s.includes("phys")) return { key: "Pendulum_waves", label: subject };
  return { key: "Brain", label: subject };
};

export const formatDate = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return new Date().toLocaleDateString();
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

// Starfield dots for the dark frame (deterministic so it looks intentional).
export const STARS = Array.from({ length: 60 }, (_, i) => {
  const x = (i * 61.8) % 100;
  const y = (i * 38.2 + (i % 5) * 7) % 100;
  const s = [1, 1, 1.5, 2, 1][i % 5];
  const o = [0.25, 0.4, 0.6, 0.85, 0.5][i % 5];
  return `<i style="left:${x.toFixed(2)}%;top:${y.toFixed(2)}%;width:${s}px;height:${s}px;opacity:${o}"></i>`;
}).join("");

export const buildCertHtml = ({ studentName, dateStr, code, davinciUri, sarasUri, modelImgs, autoPrint }) => {
  // Subject 3D objects: free-floating (no circles), tilted, varied sizes.
  const OBJ_POS = [
    "top:50%; left:16px; transform:translateY(-50%) rotate(-9deg);", // left, big
    "top:12%; right:16px; transform:rotate(8deg);", // right, upper
    "bottom:10%; right:18px; transform:rotate(-7deg);", // right, lower
    "bottom:10%; left:18px; transform:rotate(7deg);", // left, lower (4th+)
  ];
  const floats = modelImgs
    .map(
      (m, i) =>
        `<img class="obj${i === 0 ? " big" : ""}" src="${m.url}" alt="${m.label}" style="${OBJ_POS[i % OBJ_POS.length]}" />`
    )
    .join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Certificate — ${studentName}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${FONTS_HREF}" rel="stylesheet">
<style>
  @page { size: landscape; margin: 0; }
  :root {
    --ink:#1b2c57; --navy:#0b2a63; --gold:#c69a3a; --gold-lt:#e7c873; --gold-dk:#9c6f1c;
    --cream:#fdf8ec; --paper:#fffdf6; --slate:#5b678a;
  }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin:0; height:100%; }
  body {
    font-family: 'Jost', system-ui, sans-serif; color: var(--ink);
    display:flex; align-items:center; justify-content:center; min-height:100vh;
    background: linear-gradient(160deg,#e9edf5 0%, #dfe6f1 100%);
  }
  .stage { position:relative; width:100%; padding:28px; }

  /* Navy starfield FRAME — the cosmic border around the cream certificate */
  .frame {
    position:relative; max-width:1120px; margin:0 auto; padding:58px; overflow:hidden; border-radius:22px;
    background:
      radial-gradient(38% 46% at 16% 22%, rgba(99,102,241,.38), transparent 60%),
      radial-gradient(40% 48% at 84% 24%, rgba(45,212,191,.30), transparent 60%),
      radial-gradient(46% 52% at 72% 86%, rgba(236,72,153,.24), transparent 60%),
      radial-gradient(40% 50% at 24% 84%, rgba(231,200,115,.22), transparent 60%),
      linear-gradient(160deg,#0c2050 0%, #081634 100%);
    box-shadow: 0 0 0 2px var(--gold), 0 0 0 5px #061130, 0 26px 70px rgba(0,0,0,.5);
  }
  .fstars { position:absolute; inset:0; overflow:hidden; pointer-events:none; z-index:0; }
  .fstars i { position:absolute; border-radius:50%; background:#dce8ff; box-shadow:0 0 6px rgba(220,232,255,.7); }
  .spark { position:absolute; color:rgba(231,200,115,.55); pointer-events:none; z-index:1; }

  /* Cream parchment center */
  .panel {
    position:relative; z-index:2; max-width:1060px; margin:0 auto; padding:32px 124px 26px;
    text-align:center; color:var(--ink);
    background: radial-gradient(120% 85% at 50% 0%, #f3f7ff 0%, var(--cream) 72%);
    border-radius:10px;
    box-shadow: 0 0 0 2px var(--gold-lt), 0 0 0 4px var(--gold), 0 18px 44px rgba(11,28,68,.28);
  }
  /* Navy inner border line (blue accent inside the certificate) */
  .panel::before { content:""; position:absolute; inset:9px; border:1.5px solid rgba(11,42,99,.55);
    border-radius:6px; pointer-events:none; z-index:1; }

  /* Ornamental corner brackets */
  .corner { position:absolute; width:40px; height:40px; border:2px solid var(--gold); z-index:3; }
  .corner.tl { top:14px; left:14px; border-right:0; border-bottom:0; }
  .corner.tr { top:14px; right:14px; border-left:0; border-bottom:0; }
  .corner.bl { bottom:14px; left:14px; border-right:0; border-top:0; }
  .corner.br { bottom:14px; right:14px; border-left:0; border-top:0; }
  .corner::after { content:"✦"; position:absolute; color:var(--gold); font-size:11px; }
  .corner.tl::after { top:-3px; left:-3px; } .corner.tr::after { top:-3px; right:-3px; }
  .corner.bl::after { bottom:-3px; left:-3px; } .corner.br::after { bottom:-3px; right:-3px; }

  .logos { display:flex; justify-content:space-between; align-items:center; min-height:56px; }
  .logos img { height:52px; object-fit:contain; }
  .eyebrow { font-size:11px; letter-spacing:5px; text-transform:uppercase; color:var(--gold-dk); font-weight:600; margin-top:6px; }

  .title { font-family:'Cormorant Garamond', serif; font-weight:700; font-size:64px;
           letter-spacing:8px; line-height:1; margin:2px 0 0; text-transform:uppercase;
           background:linear-gradient(180deg,#21539f 0%, #163d86 55%, #0b2a63 100%);
           -webkit-background-clip:text; background-clip:text; color:transparent; }
  .of { font-family:'Pinyon Script', cursive; font-size:44px; line-height:.9; margin-top:-12px; color:#a9781f;
        transform:rotate(-3deg); }
  .divider { display:flex; align-items:center; justify-content:center; gap:12px; margin:8px auto 12px; max-width:420px; }
  .divider .line { height:1px; flex:1; background:linear-gradient(90deg, transparent, var(--gold), transparent); }
  .divider .dot { color:var(--gold); font-size:13px; }
  .ribbon { position:relative; display:inline-block; padding:7px 30px; border-radius:2px; font-size:12px; letter-spacing:4px;
            font-weight:600; color:#fff; margin-top:2px;
            background:linear-gradient(180deg,#15387f,#0b2a63); box-shadow:0 3px 7px rgba(7,21,58,.4); }
  .ribbon::before, .ribbon::after { content:""; position:absolute; top:50%; transform:translateY(-50%);
            width:24px; height:30px; background:#0a2257; z-index:-1; }
  .ribbon::before { left:-15px; clip-path:polygon(0 0,100% 0,100% 100%,0 100%,42% 50%); }
  .ribbon::after { right:-15px; clip-path:polygon(0 0,100% 0,58% 50%,100% 100%,0 100%); }

  .presented { font-size:12px; letter-spacing:3px; text-transform:uppercase; color:var(--slate); margin:16px 0 2px; }
  .name { font-family:'Pinyon Script', cursive; font-size:74px; line-height:1; color:var(--navy); margin:2px 0 2px; }
  .flourish { display:flex; justify-content:center; margin:6px auto 12px; }
  .flourish svg { display:block; }
  .desc { font-family:'Cormorant Garamond', serif; font-size:19px; font-style:italic; color:#33406a;
          max-width:680px; margin:0 auto 12px; line-height:1.45; }

  /* Subject 3D objects — free-floating, tilted, with a soft shadow */
  .obj { position:absolute; width:86px; height:86px; object-fit:contain; z-index:4;
         filter: drop-shadow(0 8px 14px rgba(11,42,99,.4)); }
  .obj.big { width:104px; height:104px; }

  .signs { display:flex; justify-content:space-between; align-items:flex-end; gap:36px; margin:14px 30px 2px; }
  .sign { flex:1; text-align:center; }
  .sign .sig { font-family:'Pinyon Script', cursive; font-size:30px; color:var(--navy); line-height:1; min-height:34px; }
  .sign .plain { font-size:16px; color:var(--ink); min-height:34px; line-height:34px; }
  .sign .rule { border-top:1.5px solid var(--navy); padding-top:5px; margin-top:2px; }
  .sign .role { font-size:10px; letter-spacing:2px; text-transform:uppercase; color:var(--slate); font-weight:600; }
  .serial { margin-top:12px; font-family:'Jost', sans-serif; font-size:9.5px; letter-spacing:2.5px;
            text-transform:uppercase; color:var(--slate); font-weight:500; }

  /* Print: the certificate fills the page (no blue around it). */
  @media print {
    body { min-height:auto; display:block; background:#ffffff !important; }
    .stage { padding:0; }
    .panel { margin:0 auto; max-width:100%; border-radius:0; }
  }
</style>
</head>
<body ${autoPrint ? `onload="setTimeout(function(){ try{window.focus();window.print();}catch(e){} }, 600);"` : ""}>
  <div class="stage">
      <div class="panel">
        <span class="corner tl"></span><span class="corner tr"></span>
        <span class="corner bl"></span><span class="corner br"></span>
        ${floats}

        <div class="logos">
          ${davinciUri ? `<img src="${davinciUri}" alt="Da Vinci Science Center" />` : "<span></span>"}
          ${sarasUri ? `<img src="${sarasUri}" alt="Saras-3D" />` : "<span></span>"}
        </div>

        <div class="eyebrow">Da Vinci Science Center &nbsp;✦&nbsp; Saras&#8209;3D</div>
        <div class="title">Certificate</div>
        <div class="of">of Completion</div>
        <div class="divider"><span class="line"></span><span class="dot">✦</span><span class="line"></span></div>
        <div class="ribbon">THREE&#8209;DAY&nbsp;&nbsp;CHALLENGE</div>

        <div class="presented">This certificate is proudly presented to</div>
        <div class="name">${studentName}</div>
        <div class="flourish">
          <svg width="260" height="22" viewBox="0 0 260 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 13 C 60 1, 95 1, 130 11 C 165 21, 200 21, 250 9" stroke="#c69a3a" stroke-width="1.4" stroke-linecap="round" fill="none"/>
            <circle cx="130" cy="11" r="3.4" fill="#c69a3a"/>
            <path d="M4 11 q4 -6 9 -1" stroke="#c69a3a" stroke-width="1.2" stroke-linecap="round" fill="none"/>
            <path d="M256 11 q-4 -6 -9 -1" stroke="#c69a3a" stroke-width="1.2" stroke-linecap="round" fill="none"/>
          </svg>
        </div>
        <div class="desc">
          for completing the Three&#8209;Day Challenge with curiosity, creativity, critical
          thinking, and a genuine passion for discovery.
        </div>

        <div class="signs">
          <div class="sign"><div class="sig">Lin Erickson</div><div class="rule"><div class="role">Da Vinci Science Center</div></div></div>
          <div class="sign"><div class="sig">Bipin Dama</div><div class="rule"><div class="role">Saras-3D</div></div></div>
          <div class="sign"><div class="plain">${dateStr}</div><div class="rule"><div class="role">Date Awarded</div></div></div>
        </div>
        ${code ? `<div class="serial">Certificate No.&nbsp;&nbsp;${code}</div>` : ""}
      </div>
  </div>
</body>
</html>`;
};
